import { useState, useEffect } from 'react';
import { animStyle } from '../../../hooks/useScrollReveal';

export default function Hero() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 120);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative w-full h-screen overflow-hidden flex items-end pb-24 md:pb-20">
      {/* Video background */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src="https://video.wixstatic.com/video/a314f7_45d3fc9aab2e4d38bb844380b8dd76ad/720p/mp4/file.mp4"
        poster="https://static.wixstatic.com/media/a314f7_45d3fc9aab2e4d38bb844380b8dd76adf000.jpg/v1/fill/w_853,h_720,al_c,q_85,usm_0.33_1.00_0.00,enc_avif/a314f7_45d3fc9aab2e4d38bb844380b8dd76adf000.jpg"
        autoPlay
        loop
        muted
        playsInline
      />

      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.3) 55%, rgba(0,0,0,0.1) 100%)',
          opacity: mounted ? 1 : 0.95,
          transition: 'opacity 1.8s ease-out',
        }}
      />

      {/* Scroll-down indicator — bottom center */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10"
        style={animStyle(mounted, 'fade', 1600, 700)}
      >
        <span className="text-white/30 text-[9px] tracking-[0.3em] font-['Montserrat'] mb-1">SCROLL</span>
        <div className="flex flex-col items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-px bg-white/50"
              style={{ height: '7px', animation: `scrollPulse 1.8s ease-in-out ${i * 0.22}s infinite` }}
            />
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full px-5 sm:px-8 md:px-20">
        {/* Accent line */}
        <div
          style={{
            height: '1px',
            backgroundColor: 'rgba(255,255,255,0.45)',
            width: mounted ? '40px' : '0px',
            transition: 'width 0.8s ease-out 0.2s',
            marginBottom: '16px',
          }}
        />

        <h1
          className="text-4xl sm:text-5xl md:text-7xl font-light text-white tracking-widest mb-3 md:mb-4 font-['Montserrat'] leading-tight"
          style={animStyle(mounted, 'up', 300, 900)}
        >
          Groove Gravity
        </h1>

        <h2
          className="text-base sm:text-lg md:text-2xl text-white/90 font-light tracking-wider mb-4 md:mb-5 leading-relaxed"
          style={animStyle(mounted, 'up', 500, 900)}
        >
          人と人をつなぐ<br className="sm:hidden" />ソーシャルメディアマーケティング
        </h2>

        {/* Divider */}
        <div
          style={{
            height: '1px',
            backgroundColor: 'rgba(255,255,255,0.5)',
            width: mounted ? '48px' : '0px',
            transition: 'width 0.7s ease-out 720ms',
            marginBottom: '12px',
          }}
        />

        <p
          className="text-[10px] sm:text-xs text-white/70 tracking-[0.25em] sm:tracking-[0.3em] font-['Montserrat'] font-light mb-8 md:mb-10"
          style={animStyle(mounted, 'fade', 820, 900)}
        >
          DESIGN THE FUTURE WITH SOCIAL MEDIA
        </p>

        {/* CTA buttons */}
        <div
          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
          style={animStyle(mounted, 'up', 1050, 800)}
        >
          <button
            onClick={() => scrollTo('services')}
            className="group relative flex items-center justify-center gap-3 bg-white text-black text-xs font-medium tracking-[0.2em] px-8 py-4 sm:py-3.5 overflow-hidden cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-white/90 w-full sm:w-auto"
          >
            <span className="relative z-10">サービスを見る</span>
            <i className="ri-arrow-right-line text-sm relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
          </button>

          <button
            onClick={() => scrollTo('contact')}
            className="flex items-center justify-center gap-3 border border-white/50 text-white text-xs tracking-[0.2em] px-8 py-4 sm:py-3.5 hover:border-white hover:bg-white/8 transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
          >
            <span>お問い合わせ</span>
            <i className="ri-mail-line text-sm text-white/60" />
          </button>
        </div>

        {/* Service quick-links — hidden on very small screens */}
        <div
          className="mt-6 hidden sm:flex flex-wrap items-center gap-x-5 gap-y-2"
          style={animStyle(mounted, 'fade', 1280, 700)}
        >
          {['ソーシャルメディア運用', '各種広告運用', 'コンテンツ制作', 'イベント・キャンペーン'].map((label, i) => (
            <button
              key={i}
              onClick={() => scrollTo('services')}
              className="flex items-center gap-1.5 text-white/40 hover:text-white/80 text-[11px] tracking-wider transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <span className="inline-block w-1 h-1 rounded-full bg-white/30" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.15; }
          50%       { opacity: 0.8; }
        }
      `}</style>
    </section>
  );
}
